/**
 * Gemeinsame Formular-Bausteine für Bedarfszeilen — genutzt vom Event-Editor (.dbrow je Zeile) und vom
 * Bedarf-Dialog (Bedarf anlegen/bearbeiten in bedarfZuordnung.js), damit beide gleich funktionieren.
 */

import dayjs from '../../../../shared/services/dayjs.js';

/** Kombiniert Tag (YYYY-MM-DD) + Uhrzeit (HH:mm) zu einer ISO-Zeit; Bis <= Von rutscht auf den Folgetag. */
function toIso(tag, zeit, istBis = false, vonZeit = null) {
	let d = `${tag}T${zeit}:00`;
	if (istBis && vonZeit && zeit <= vonZeit) {
		d = `${dayjs(tag).add(1, 'day').format('YYYY-MM-DD')}T${zeit}:00`;
	}
	return d;
}

/**
 * Stepper (+/-) für die Bedarfsanzahl.
 * @param {object} optionen
 * @param {number} optionen.wert Startwert
 * @param {number} [optionen.min] kleinster erlaubter Wert (z.B. Zahl der bereits Zugeordneten)
 * @param {function} optionen.onChange (neuerWert) => void
 */
function baueStepper({ wert, min = 1, onChange }) {
	let anzahl = wert;
	const wrap = document.createElement('div');
	wrap.className = 'stepper';
	const minus = document.createElement('span');
	minus.className = 'sb';
	minus.innerHTML = '<i class="bi bi-dash"></i>';
	const n = document.createElement('span');
	n.className = 'n';
	n.textContent = anzahl;
	const plus = document.createElement('span');
	plus.className = 'sb';
	plus.innerHTML = '<i class="bi bi-plus"></i>';
	const aktualisiere = () => {
		n.textContent = anzahl;
		minus.classList.toggle('off', anzahl <= min);
		minus.title = anzahl <= min && min > 1 ? `Mindestens ${min} — so viele sind bereits zugeordnet` : '';
	};
	minus.addEventListener('click', () => { if (anzahl > min) { anzahl -= 1; aktualisiere(); onChange(anzahl); } });
	plus.addEventListener('click', () => { anzahl += 1; aktualisiere(); onChange(anzahl); });
	wrap.append(minus, n, plus);
	aktualisiere();
	return wrap;
}

/**
 * Posten-Select, nach Bereich gruppiert (der Bereich ergibt sich aus dem Posten).
 * @param {object} optionen
 * @param {array} optionen.bereiche aktive Bereiche
 * @param {array} optionen.postenListe alle Posten
 * @param {string} optionen.postenID ausgewählter Posten
 * @param {boolean} [optionen.gesperrt] deaktiviert (z.B. solange Mitarbeitende zugeordnet sind)
 * @param {string} [optionen.className]
 * @param {function} optionen.onChange ({ postenID, posten, bereichID, bereich }) => void
 */
function bauePostenSelect({ bereiche, postenListe, postenID, gesperrt = false, className = 'ptsel', onChange }) {
	const sel = document.createElement('select');
	sel.className = className;
	sel.disabled = gesperrt;
	bereiche.forEach((b) => {
		const postenDesBereichs = postenListe.filter((p) => p.BereichID === b._id);
		if (postenDesBereichs.length === 0) return;
		const grp = document.createElement('optgroup');
		grp.label = b.Bereich;
		postenDesBereichs.forEach((p) => {
			const opt = document.createElement('option');
			opt.value = p._id;
			opt.textContent = p.Posten;
			if (p._id === postenID) opt.selected = true;
			grp.appendChild(opt);
		});
		sel.appendChild(grp);
	});
	sel.addEventListener('change', () => onChange(postenInfo(bereiche, postenListe, sel.value)));
	return sel;
}

/** Posten + zugehöriger Bereich zu einer PostenID. */
function postenInfo(bereiche, postenListe, postenID) {
	const posten = postenListe.find((p) => p._id === postenID);
	const bereich = posten ? bereiche.find((b) => b._id === posten.BereichID) : null;
	return {
		postenID,
		posten: posten?.Posten || '',
		bereichID: posten?.BereichID || '',
		bereich: bereich?.Bereich || '',
	};
}

/** Hinweis, warum der Posten gesperrt ist (unter dem Posten-Select). */
function postenGesperrtHinweis(anzahlZugeordnet) {
	const el = document.createElement('div');
	el.className = 'lockhint';
	el.innerHTML = `<i class="bi bi-lock-fill"></i>Posten kann nicht geändert werden, solange Mitarbeitende zugeordnet sind (${anzahlZugeordnet}). Zuordnungen zuerst entfernen.`;
	return el;
}

function baueZeitInput(wert, onChange, className = 'ti') {
	const inp = document.createElement('input');
	inp.type = 'time';
	inp.className = className;
	inp.value = wert;
	inp.addEventListener('change', () => onChange(inp.value));
	return inp;
}

function feld(label, inner) {
	const f = document.createElement('div');
	f.className = 'fld';
	const l = document.createElement('label');
	l.textContent = label;
	f.append(l, inner);
	return f;
}

function textInput(wert, onChange, platzhalter = '') {
	const inp = document.createElement('input');
	inp.className = 'inp';
	inp.type = 'text';
	inp.value = wert || '';
	inp.placeholder = platzhalter;
	inp.addEventListener('input', () => onChange(inp.value));
	return inp;
}

function textArea(wert, onChange) {
	const ta = document.createElement('textarea');
	ta.className = 'inp';
	ta.style.cssText = 'height:auto;min-height:60px;padding:10px 12px;';
	ta.value = wert || '';
	ta.addEventListener('input', () => onChange(ta.value));
	return ta;
}

/**
 * Select mit Leer-Option.
 * @param {array<{value: string, text: string}>} optionen
 * @param {string} wert ausgewählter Wert
 * @param {string} leerText Beschriftung der Leer-Option (Wert '')
 * @param {function} onChange (wert) => void
 * @param {string} [className]
 */
function baueSelect(optionen, wert, leerText, onChange, className = 'inp') {
	const sel = document.createElement('select');
	sel.className = className;
	const leer = document.createElement('option');
	leer.value = '';
	leer.textContent = leerText;
	sel.appendChild(leer);
	optionen.forEach(({ value, text }) => {
		const opt = document.createElement('option');
		opt.value = value;
		opt.textContent = text;
		if (value === wert) opt.selected = true;
		sel.appendChild(opt);
	});
	sel.addEventListener('change', () => onChange(sel.value));
	return sel;
}

export {
	toIso,
	baueStepper,
	bauePostenSelect,
	postenInfo,
	postenGesperrtHinweis,
	baueZeitInput,
	feld,
	textInput,
	textArea,
	baueSelect,
};
